// Política ÚNICA del tope de imágenes por producto.
//
// Igual que `normalizeImageForUpload` con los formatos: el número y la regla
// viven acá y los ~6 puntos de carga los consumen. Un `.slice(0, N)` repetido en
// cada pantalla se desincroniza en el primer cambio de criterio.
//
// El tope existe porque el backend manda las fotos del producto a la IA en cada
// generación (`IMAGES_PER_VARIANT` en atlas-backend) y las de más nunca se usan:
// eran storage y espera que el usuario pagaba sin recibir nada a cambio. 5 es el
// número que el backend consume (`MAX_FOTOS_POR_PRODUCTO` en atlas-backend) —
// cargar y usar quedan alineados. Anuncios, ambiente completo y pose siguen
// mirando 4 por producto a propósito (ahí se reparten 10 referencias).
// Ver ADR-2026-08-17-tope-4-imagenes-por-producto y
// ADR-2026-09-29-tope-5-fotos-por-producto (que lo sube de 4 a 5).
export const MAX_IMAGENES_POR_PRODUCTO = 5;

/**
 * Cuántas imágenes más acepta un producto que ya tiene `yaCargadas`.
 *
 * El `Math.max(0, ...)` no es defensivo de más: los productos cargados ANTES del
 * tope conservan sus fotos (no se migró nada), así que `yaCargadas` puede ser 6 o
 * 445. Sin el clamp, `slice(0, 5 - 7)` recorta desde el final en vez de devolver
 * vacío y dejaría entrar archivos justo en el caso que hay que bloquear.
 */
export function cupoRestante(yaCargadas: number): number {
  return Math.max(0, MAX_IMAGENES_POR_PRODUCTO - yaCargadas);
}

/**
 * Parte un lote de archivos en lo que entra y lo que sobra, preservando el orden
 * elegido por el usuario: la primera del lote es la que queda como `principal`, y
 * el backend le manda las fotos a la IA en orden `principal desc, orden asc`.
 */
export function recortarAlTope<T>(
  yaCargadas: number,
  nuevas: T[],
): { aceptadas: T[]; rechazadas: T[] } {
  const cupo = cupoRestante(yaCargadas);
  return { aceptadas: nuevas.slice(0, cupo), rechazadas: nuevas.slice(cupo) };
}

/**
 * Aviso para el usuario cuando el lote se recortó. `null` = no hubo recorte.
 * El recorte NUNCA es silencioso: el usuario eligió esos archivos y si no le
 * avisamos cree que están subidos.
 */
export function mensajeRecorte(rechazadas: number): string | null {
  if (rechazadas <= 0) return null;
  const plural = rechazadas === 1 ? "imagen" : "imágenes";
  return `Máximo ${MAX_IMAGENES_POR_PRODUCTO} imágenes por producto: no subimos ${rechazadas} ${plural}.`;
}
